import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Calculator, Camera, KeyRound, Mail, Save, Trash2, UserCircle2 } from "lucide-react";
import { API } from "../lib/apiClient";
import { UserAvatar } from "./UserAvatar";
import { AvatarCropModal } from "./AvatarCropModal";
import { PushSettingsCard } from "./PushSettingsCard";
import { WaTemplatesCard } from "./WaTemplatesCard";
import { CoverageTemplatesCard } from "./CoverageTemplatesCard";
import { BusinessCardSettingsCard } from "./BusinessCardSettingsCard";
import { CompaniesCard, EmailQueueCard, UndeliverableCard } from "./SettingsExtras";
import { PageTitle, Field } from "./common";
import { money2 } from "../lib/finance";
import { CronStatusPanel } from "./CronStatusPanel";

function AccountCard({ user, onChangePassword, onChangeEmail, onUserUpdated }) {
  const [form, setForm] = useState({ name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);
  useEffect(() => { setForm({ name: user?.name || "", phone: user?.phone || "" }); }, [user?.name, user?.phone]);
  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 3000); };
  const run = async (fn, okText) => {
    setBusy(true);
    try { await fn(); await onUserUpdated?.(); flash(okText); }
    catch (e) { flash(`Hata: ${e?.response?.data?.detail || e?.message}`); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const saveProfile = (e) => {
    e.preventDefault();
    run(() => axios.put(`${API}/user/profile`, form), "Profil kaydedildi · imza bilgileri güncellendi");
  };
  const [cropFile, setCropFile] = useState(null);
  const upload = (file) => {
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) { flash("Sadece PNG, JPG veya WebP"); return; }
    if (file.size > 10 * 1024 * 1024) { flash("Fotoğraf en fazla 10MB olabilir"); return; }
    setCropFile(file);
  };
  const saveCropped = async (blob) => {
    const fd = new FormData();
    fd.append("file", blob, "avatar.jpg");
    await run(() => axios.post(`${API}/user/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } }), "Profil fotoğrafı güncellendi");
    setCropFile(null);
  };
  const removePhoto = () => {
    if (!window.confirm("Profil fotoğrafını kaldırmak istiyor musunuz?")) return;
    run(() => axios.delete(`${API}/user/avatar`), "Fotoğraf kaldırıldı");
  };
  const dirty = form.name !== (user?.name || "") || form.phone !== (user?.phone || "");
  return (
    <section className="panel settings-card" data-testid="settings-account-card">
      <div className="settings-card-head"><UserCircle2 size={16} /> <h2>Hesabım</h2></div>
      <div className="account-row">
        <UserAvatar user={user} className="avatar xlarge" testId="account-avatar" />
        <div className="account-info">
          <strong data-testid="account-name">{user?.name || "—"}</strong>
          <span data-testid="account-email">{user?.email || "—"}</span>
          <em className="account-role" data-testid="account-role">{user?.role === "admin" ? "Yönetici" : "Kullanıcı"}</em>
          <div className="account-photo-actions">
            <input type="file" ref={fileRef} accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} data-testid="avatar-file-input" onChange={(e) => upload(e.target.files?.[0])} />
            <button type="button" className="text-button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="avatar-upload-button"><Camera size={13} /> {user?.avatarUrl ? "Fotoğrafı değiştir" : "Fotoğraf yükle"}</button>
            {user?.avatarUrl && <button type="button" className="text-button" style={{ color: "#dc2626" }} disabled={busy} onClick={removePhoto} data-testid="avatar-remove-button"><Trash2 size={13} /> Kaldır</button>}
          </div>
        </div>
      </div>
      <form onSubmit={saveProfile}>
        <div className="form-grid">
          <Field label="Ad soyad" name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} hint="Menüde ve e-posta imzanızda görünür" required />
          <Field label="Telefon" name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} hint="E-posta imzanızda da kullanılır" />
        </div>
        <div className="settings-actions">
          <button className="primary-button" disabled={busy || !dirty} data-testid="settings-profile-save"><Save size={14} /> Profili kaydet</button>
          <button type="button" className="outline-button" onClick={onChangePassword} data-testid="settings-change-password"><KeyRound size={14} /> Şifre değiştir</button>
          <button type="button" className="outline-button" onClick={onChangeEmail} data-testid="settings-change-email"><Mail size={14} /> E-posta değiştir</button>
        </div>
        {msg && <small className="signature-hint" data-testid="settings-profile-msg">{msg}</small>}
      </form>
      {cropFile && <AvatarCropModal file={cropFile} onCancel={() => setCropFile(null)} onConfirm={saveCropped} />}
    </section>
  );
}

export function CalcSettingsCard({ settings, onSave }) {
  const [form, setForm] = useState({ netDivisor: "", personalShare: "" });
  const [msg, setMsg] = useState("");
  useEffect(() => {
    setForm({ netDivisor: String(settings?.netDivisor ?? ""), personalShare: String(settings?.personalShare ?? "") });
  }, [settings?.netDivisor, settings?.personalShare]);
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    const ok = await onSave(form);
    setMsg(ok ? "Ayarlar kaydedildi" : "Kaydedilemedi");
    setTimeout(() => setMsg(""), 2500);
  };
  return (
    <section className="panel settings-card" data-testid="settings-calc-card">
      <div className="settings-card-head"><Calculator size={16} /> <h2>Hesaplama ayarları</h2></div>
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Net bölen (varsayılan 1.12)" name="netDivisor" type="number" step="0.01" value={form.netDivisor} onChange={change} hint="Net = Brüt ÷ bölen" required />
          <Field label="Kişisel pay (%)" name="personalShare" type="number" step="0.5" value={form.personalShare} onChange={change} hint="Kişisel = Acente komisyonu × pay" required />
        </div>
        <p className="privacy-note">Örnek: Brüt 10.000₺ · Trafik %10 · Net = 8.929₺ · Acente = 893₺ · Kişisel = {money2(893 * (Number(form.personalShare) / 100))}</p>
        <div className="settings-actions">
          <button className="primary-button" data-testid="settings-calc-save"><Save size={14} /> Ayarları kaydet</button>
          {msg && <small className="signature-hint" data-testid="settings-calc-msg">{msg}</small>}
        </div>
      </form>
    </section>
  );
}

const JUMPS = [["Hesap", "settings-page"], ["Bildirimler", "push-settings-card"], ["WhatsApp şablonları", "wa-templates-card"], ["Teminat şablonları", "coverage-templates-card"], ["QR Kartvizit", "settings-business-card"], ["Şirketler", "companies-card"], ["Branşlar", "settings-branches-section"]];

function SettingsJump() {
  const go = (id) => {
    const el = document.querySelector(`[data-testid="${id}"]`);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - (id === "settings-page" ? 90 : 80), behavior: "smooth" });
  };
  return (
    <nav className="settings-jump" data-testid="settings-jump">
      {JUMPS.map(([l, id]) => <button key={id} type="button" onClick={() => go(id)} data-testid={`settings-jump-${id}`}>{l}</button>)}
    </nav>
  );
}

export function SettingsPage({ user, onUserUpdated, onEditCustomer, settings, onSaveSettings, canEditSettings, onChangePassword, onChangeEmail }) {
  return (
    <div data-testid="settings-page">
      <PageTitle eyebrow="AYARLAR" title="Ayarlar" description="Hesabınız, hesaplama ayarları, otomatik hatırlatmalar, e-posta bildirimleri, logo, imza ve teslim kayıtları." />
      <SettingsJump />
      <AccountCard user={user} onUserUpdated={onUserUpdated} onChangePassword={onChangePassword} onChangeEmail={onChangeEmail} />
      <PushSettingsCard />
      <WaTemplatesCard />
      <CoverageTemplatesCard />
      <BusinessCardSettingsCard />
      <CompaniesCard />
      <div className="settings-top-grid">
        <EmailQueueCard />
        <UndeliverableCard onEditCustomer={onEditCustomer} />
      </div>
      <CronStatusPanel key={`${user?.name}|${user?.phone}`} defaultEditing />
    </div>
  );
}
